import {
  CheckmarkIcon,
  Col,
  Container,
  Space,
  Typography,
  getTypographyDefaultColor
} from '@ag1/ui';
import { SoldOutProductsBanner } from '@portal/shared/components/SoldOutProductsBanner';
import { getPrice } from '@portal/shared/utils/customer';
import { isDoubleSubscription } from '@portal/shared/utils/check/isDoubleSubscription';
import { useIsStore } from '@portal/state/hooks/useIsStore';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import {
  getOfferAvailabilityRestriction,
  getOfferSubscriptionMedia,
  getOfferSubscriptionProductData,
  getStoreCodeFromAtlasLocale,
  useLocaleForAtlas
} from '@portal/types/offer';
import { clickPurchaseIntentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { Theme } from 'constants/theme';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { usePageContext } from 'processes/Page/PageContext';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { HsaFsaAvailability } from 'shared/components/HSA-FSA/HsaFsaAvailability';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { offerSectionAtom } from 'state/atoms/offerSectionAtom';
import { useStoreValue } from 'state/hooks/useStore';
import { useTranslation } from 'react-i18next';
import { extractNumberFromCurrencyString } from '@portal/shared/studio/AddToCartSectionShop/utils';
import {
  ContentBlockButton,
  ContentBlockProps,
  ResponsiveContentBlockImage,
  useContentBlockOfferPricesItems,
  useOfferContentBlock,
  type CustomOfferPricesItem
} from '../../common';
import commonStyles from '../../styles.module.scss';
import { OfferButtonDisabledReason } from '../common';
import styles from './OfferPrice.module.scss';

const theme: Theme = 'new';

const COL_SPAN = { xxs: 12, m: 6 };

type Props = ContentBlockProps;

export const OfferPrice: React.FC<Props> = ({ slug, customContent, visibleOnPageLoad }) => {
  const { t } = useTranslation('offer');
  const {
    offer,
    subscriptionTypes,
    offerCTALink,
    shopifyVariantsByStoreCode,
    disablePrimaryCTAButtons
  } = usePageContext();
  const router = useRouter();
  const store = useStoreValue();
  const atlasLocale = useLocaleForAtlas();

  const shopifyVariants = shopifyVariantsByStoreCode?.[store];

  const setOfferSectionAtom = useSetAtom(offerSectionAtom);
  const offerSectionAtomValue = useAtomValue(offerSectionAtom);

  const sectionRef = useRef<HTMLDivElement>(null);
  const { isUsStore } = useIsStore();

  useEffect(() => {
    if (sectionRef.current) {
      setOfferSectionAtom(sectionRef.current);
    }
  }, [setOfferSectionAtom, sectionRef]);

  const availability = useMemo(
    () => getOfferAvailabilityRestriction(offer, atlasLocale),
    [offer, atlasLocale]
  );

  const offerImage = useMemo(() => {
    const primarySubscription = offer?.subscriptions.find((i) => i.type?.locale === atlasLocale);
    const media = [
      ...(primarySubscription?.media || []),
      ...(subscriptionTypes?.find((i) =>
        primarySubscription
          ? i.id === primarySubscription?.offer_subscription_type_id
          : i.locale === atlasLocale
      )?.media || []),
      ...(subscriptionTypes?.flatMap((i) => i.media) || [])
    ];
    return getOfferSubscriptionMedia(media, false);
  }, [atlasLocale, offer?.subscriptions, subscriptionTypes]);

  const {
    headline,
    backgroundColor,
    imagePosition,
    buttonLabel,
    buttonIcon,
    buttonIconPosition,
    showTrumedNote,
    showOldPriceFirst,
    captions
  } = useOfferContentBlock(customContent, { slug, router }, offer || null);

  const { symbolCurrency } = useStoreCurrency();
  const priceItems = useContentBlockOfferPricesItems(customContent, { slug, router });

  const getItemPrice = useCallback(
    (item: CustomOfferPricesItem, isPrimaryItem: boolean) => {
      // Ensure that this is a number so we're not accidentally adding strings.
      let rawPrice = parseFloat(`${item.price}`);
      const doubleSubscription = isDoubleSubscription(store, item.shopifyVariantId || '');

      if (Number.isNaN(rawPrice)) {
        rawPrice = 0;
      }
      let price = getPrice(symbolCurrency, rawPrice);
      let originalPrice = doubleSubscription
        ? getPrice(symbolCurrency, rawPrice + 49)
        : getPrice(symbolCurrency, rawPrice + 20);
      let originalRawPrice: number | undefined;

      if (!isPrimaryItem) {
        return { price, rawPrice, originalPrice };
      }

      const firstOfferSubscription = offer?.subscriptions.find(Boolean);
      let shopifyVariantId: string | undefined;
      if (firstOfferSubscription) {
        // 'type' will be missing if this was received from a section preview.
        // Instead we need to check the `subscriptionTypes`.
        if ('type' in firstOfferSubscription && firstOfferSubscription.type) {
          shopifyVariantId = getOfferSubscriptionProductData(firstOfferSubscription.type)
            ?.productVariantId;
        } else {
          const matchingSubType = subscriptionTypes?.find(
            (i) => i.id === firstOfferSubscription.offer_subscription_type_id
          );
          const productData = matchingSubType
            ? getOfferSubscriptionProductData(matchingSubType)
            : null;
          if (productData) {
            shopifyVariantId = productData.productVariantId;
          }
        }
      }
      shopifyVariantId ||= item.shopifyVariantId;

      if (shopifyVariantId) {
        const shopifyVariant = shopifyVariantsByStoreCode?.[
          getStoreCodeFromAtlasLocale(atlasLocale)
        ]?.find((i) => i.id.endsWith(shopifyVariantId!));
        if (shopifyVariant) {
          const subscriptionType = subscriptionTypes?.find(
            (i) => i.id === firstOfferSubscription?.offer_subscription_type_id
          );
          const productData = subscriptionType
            ? getOfferSubscriptionProductData(subscriptionType)
            : null;
          const quantity = productData?.quantity || item.quantity;
          const priceNumber = quantity
            ? Number(shopifyVariant?.contextualPricing?.price?.amount || shopifyVariant.price) *
              quantity
            : Number(shopifyVariant?.contextualPricing?.price?.amount || shopifyVariant.price);

          if (!Number.isNaN(priceNumber)) {
            rawPrice = priceNumber;
            price = getPrice(symbolCurrency, priceNumber);

            if (shopifyVariant.product.originalPrice) {
              const baseValue =
                extractNumberFromCurrencyString(shopifyVariant.product.originalPrice.value) ?? 0;
              originalRawPrice = baseValue * (quantity ?? 1);
              originalPrice = getPrice(symbolCurrency, originalRawPrice);
            } else {
              originalPrice = getPrice(
                symbolCurrency,
                priceNumber + (doubleSubscription ? 49 : 20)
              );
            }
          }
        }
      }

      return { price, rawPrice, originalPrice, originalRawPrice };
    },
    [
      atlasLocale,
      offer?.subscriptions,
      shopifyVariantsByStoreCode,
      subscriptionTypes,
      symbolCurrency,
      store
    ]
  );

  const itemsWithPrice = useMemo(() => {
    const primaryItem = priceItems.length ? priceItems[0] : null;
    const addons = priceItems.slice(1).map((i) => {
      const { price, rawPrice } = getItemPrice(i, false);
      return { ...i, price, rawPrice };
    });

    if (primaryItem) {
      const { price, rawPrice, originalPrice, originalRawPrice } = getItemPrice(primaryItem, true);
      return {
        primaryItem: { ...primaryItem, price, rawPrice, originalPrice, originalRawPrice },
        addons
      };
    }
    return {
      primaryItem: null,
      addons
    };
  }, [getItemPrice, priceItems]);

  // const unbundledTotal = useMemo(() => {
  //   const total = priceItems.reduce((acc, i, index) => {
  //     const { rawPrice } = getItemPrice(i, index === 0);

  //     if (!Number.isNaN(rawPrice) && rawPrice > 0) {
  //       return acc + rawPrice;
  //     }

  //     return acc;
  //   }, 0);

  //   if (total === itemsWithPrice.primaryItem?.rawPrice) {
  //     return null;
  //   }

  //   return getPrice(symbolCurrency, total);
  // }, [getItemPrice, itemsWithPrice.primaryItem?.rawPrice, priceItems, symbolCurrency]);

  const handleButtonClick = useMemo(() => {
    if (!offerCTALink && disablePrimaryCTAButtons) {
      return undefined;
    }

    return () => {
      clickPurchaseIntentEvent({
        store,
        clickText: buttonLabel || undefined,
        location: 'OfferPrice',
        url: offerCTALink || undefined
      });

      if (offerSectionAtomValue && !offerCTALink) {
        offerSectionAtomValue.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    };
  }, [buttonLabel, disablePrimaryCTAButtons, offerCTALink, offerSectionAtomValue, store]);

  const imageComponent = useMemo(() => {
    const classes = clsx(
      styles.imageCont,
      imagePosition.position === 'right' ? styles.imageLeft : styles.imageRight
    );

    return (
      <ResponsiveContentBlockImage
        className={classes}
        image={offerImage}
        sizes={COL_SPAN}
        eagerLoading={visibleOnPageLoad}
      />
    );
  }, [offerImage, imagePosition.position, visibleOnPageLoad]);

  const shouldShowOriginalPrice = useMemo(() => {
    const item = itemsWithPrice.primaryItem;
    if (!item) return false;

    // if originalPrice is set in shopify, only show if different from item price
    if (typeof item.originalRawPrice === 'number') {
      return item.originalRawPrice !== item.rawPrice;
    }

    // if originalPrice is not set in shopify, always show the fallback 20/49 values
    return true;
  }, [itemsWithPrice.primaryItem]);

  return (
    <div
      className={clsx(
        styles.root,
        imagePosition.position === 'right' && styles.reverse,
        commonStyles[backgroundColor],
        'offer-price'
      )}>
      <div className={clsx(styles.header, styles.mobile)}>
        <Typography
          variant="headlineSans"
          size="2"
          color={getTypographyDefaultColor(theme)}
          noPadding>
          {headline}
        </Typography>
      </div>

      <Col className={clsx(styles.col, styles.colImage)} span={COL_SPAN}>
        {imageComponent}
      </Col>

      <Col
        id="offers"
        ref={sectionRef}
        className={clsx(
          styles.col,
          styles.colInfo,
          imagePosition.position === 'right' ? styles.colInfoLeft : styles.colInfoRight
        )}
        span={COL_SPAN}>
        <Container
          className={clsx(
            imagePosition.position === 'right' ? styles.clearRight : styles.clearLeft
          )}>
          <div className={styles.content}>
            <div className={clsx(styles.header, styles.desktop)}>
              <Typography
                variant="headlineSans"
                size="2"
                color={getTypographyDefaultColor(theme)}
                noPadding>
                {headline}
              </Typography>
            </div>

            {itemsWithPrice.primaryItem && (
              <div className={styles.table}>
                <div
                  className={clsx(
                    styles.tableItem,
                    itemsWithPrice.addons.length > 0 && styles.top,
                    getThemeCSSClass(theme)
                  )}>
                  <Typography
                    color={getTypographyDefaultColor(theme)}
                    variant="body"
                    size="2"
                    weight="700"
                    noPadding
                    className={styles.tableHeadline}>
                    {itemsWithPrice.primaryItem.text}
                  </Typography>
                  <Typography
                    color={getTypographyDefaultColor(theme)}
                    variant="portalCaption"
                    size="1"
                    noPadding
                    className={clsx(styles.tableDescription, getThemeCSSClass(theme))}>
                    {itemsWithPrice.primaryItem.description}
                  </Typography>
                  <div
                    className={clsx(styles.tablePrice, showOldPriceFirst && styles.flexRowReverse)}>
                    <Typography
                      color={getTypographyDefaultColor(theme)}
                      variant="body"
                      size="2"
                      weight="700"
                      noPadding
                      className={styles.tablePriceAlt}>
                      {itemsWithPrice.primaryItem.price}
                    </Typography>
                    {shouldShowOriginalPrice && (
                      <Typography
                        color={getTypographyDefaultColor(theme)}
                        variant="body"
                        size="2"
                        noPadding
                        className={clsx(styles.tablePriceBasic, getThemeCSSClass(theme))}>
                        <s>{itemsWithPrice.primaryItem.originalPrice}</s>
                        <sup>†</sup>
                      </Typography>
                    )}
                  </div>
                </div>

                {itemsWithPrice.addons.length > 0 && (
                  <Typography
                    color={getTypographyDefaultColor(theme)}
                    variant="captionMono"
                    size="1"
                    className={clsx(styles.tableItemsHeadline, getThemeCSSClass(theme))}>
                    {t('offerPrice.firstTimePurchaseIncludes')}
                  </Typography>
                )}

                {itemsWithPrice.addons.map(
                  (item, index) =>
                    item.text && (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={index} className={styles.tableItem}>
                        <Typography
                          color={getTypographyDefaultColor(theme)}
                          variant="body"
                          size="2"
                          weight="700"
                          noPadding
                          className={styles.tableHeadline}>
                          {item.text}
                        </Typography>
                        <Typography
                          color={getTypographyDefaultColor(theme)}
                          variant="portalCaption"
                          size="1"
                          noPadding
                          className={styles.tableDescription}>
                          {item.description}
                        </Typography>
                        <div
                          className={clsx(
                            styles.tablePrice,
                            showOldPriceFirst && styles.flexRowReverse
                          )}>
                          <Typography
                            color={getTypographyDefaultColor(theme)}
                            variant="body"
                            size="2"
                            weight="700"
                            noPadding
                            className={styles.tablePriceAlt}>
                            {t('offerPrice.free')}
                          </Typography>
                          {item.rawPrice > 0 && (
                            <Typography
                              color={getTypographyDefaultColor(theme)}
                              variant="body"
                              size="2"
                              noPadding
                              className={clsx(styles.tablePriceBasic, getThemeCSSClass(theme))}>
                              <s>{item.price}</s>
                            </Typography>
                          )}
                        </div>
                      </div>
                    )
                )}

                <div className={clsx(styles.tableItem, styles.bottom, getThemeCSSClass(theme))}>
                  <Typography
                    color={getTypographyDefaultColor(theme)}
                    variant="body"
                    size="2"
                    weight="700"
                    noPadding
                    className={styles.tableHeadline}>
                    {t('offerPrice.total')}
                  </Typography>
                  <div className={styles.tablePrice}>
                    <Typography
                      color={getTypographyDefaultColor(theme)}
                      variant="body"
                      size="2"
                      weight="700"
                      noPadding
                      className={styles.tablePriceAlt}>
                      {itemsWithPrice.primaryItem.price}
                    </Typography>
                    {/* {!!unbundledTotal && (
                      <Typography
                        color={getTypographyDefaultColor(theme)}
                        variant="body"
                        size="2"
                        noPadding
                        className={clsx(styles.tablePriceBasic, getThemeCSSClass(theme))}>
                        {unbundledTotal}
                      </Typography>
                    )} */}
                  </div>
                </div>
              </div>
            )}

            {!!shopifyVariants && !!offer && (
              <SoldOutProductsBanner
                className={styles.proudctsSoldOutBanner}
                isOfferSoldOut={availability?.is_sold_out}
                variants={shopifyVariants}
              />
            )}
            {isUsStore && shouldShowOriginalPrice && (
              <Typography
                color={getTypographyDefaultColor(theme)}
                variant="portalCaption"
                size="1"
                noPadding
                className={clsx(styles.disclaimer, getThemeCSSClass(theme))}>
                <sup>†</sup> {t('offerPrice.valueOfOneTimePurchase')}
              </Typography>
            )}

            <OfferButtonDisabledReason
              style={{
                marginTop: `24px`,
                marginBottom: `-12px`
              }}
              offer={offer || null}
            />
            {buttonLabel && (
              <Space top={{ xxs: 16, s: 32, l: 40 }} className={styles.buttonWrapper}>
                <ContentBlockButton
                  className={styles.button}
                  button={{
                    label: buttonLabel,
                    link: offerCTALink || undefined
                  }}
                  fluid
                  customization={{
                    size: { xxs: 'medium', l: 'large' },
                    theme,
                    icon: buttonIcon,
                    iconPosition: buttonIconPosition,
                    onClick: handleButtonClick
                  }}
                />
              </Space>
            )}

            {showTrumedNote && <HsaFsaAvailability sourcePage="primaryLander" />}

            {captions.length > 0 && (
              <div className={styles.guaranteesWrapper}>
                {captions.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className={styles.guarantee}>
                    <Space right={4} className={styles.guaranteeIcon}>
                      <CheckmarkIcon />
                    </Space>

                    <Typography
                      color={getTypographyDefaultColor(theme)}
                      variant="portalCaption"
                      size="1"
                      noPadding>
                      {item.text}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Container>
      </Col>
    </div>
  );
};
